
import { useEffect, useMemo, useState } from 'react'
import LoaderComponent from '../../../../../../components/loader/LoaderComponent'
import LogsTableComponent from '../../../../../../components/ui/table/LogsTableComponent'
import Authentication from '../../../../../../libs/api/services/Authentication'
import WorkFlow from '../../../../../../libs/api/services/WorkFlow'

const formatTriggerProperties = (properties) => {
    if (!properties || properties.length === 0) return 'No trigger properties configured.'
    return (
        <table className='table table-sm'>
            <thead>
                <tr>
                    <th>Property</th>
                    <th>Value</th>
                </tr>
            </thead>
            <tbody>
                {properties.map((prop, idx) => (
                    <tr key={idx}>
                        <td>{prop.property || prop.name || 'property'}</td>
                        <td>{typeof prop.value === 'string' ? prop.value : JSON.stringify(prop.value)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

const formatTaskProperties = (properties) => {
    if (!properties || properties.length === 0) return 'No task properties configured.'
    return (
        <table className='table table-sm'>
            <thead>
                <tr>
                    <th>Property</th>
                    <th>Value</th>
                </tr>
            </thead>
            <tbody>
                {properties.map((prop, idx) => (
                    <tr key={idx}>
                        <td>{prop.property || prop.name || 'property'}</td>
                        <td>{typeof prop.value === 'string' ? prop.value : JSON.stringify(prop.value)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

const WorkflowItem = ({ category, event }) => {
    const _event = event && event.object ? event.object : event
    const triggers = _event?.triggers || []
    const tasks = _event?.tasks || []

    return (
        <div className='border rounded p-3 mb-3 bg-white shadow-sm'>
            <div className='d-flex justify-content-between align-items-start mb-2'>
                <div>
                    <h3 className='mb-1'>{_event?.name || 'Unnamed workflow'}</h3>
                    <div className='text-muted'>Category: {category?.name || 'Uncategorized'}</div>
                </div>
                <div className='text-end'>
                    <div><strong>Workflow ID</strong></div>
                    <div className='text-muted'>{_event?.id || 'N/A'}</div>
                </div>
            </div>

            <div className='mb-3'>
                <h5>Triggers</h5>
                {triggers.length === 0 && <div className='text-muted'>No triggers configured.</div>}
                {triggers.map((trigger, index) => (
                    <div key={trigger.id || index} className='mb-3 p-2 rounded border'>
                        <div className='d-flex justify-content-between'>
                            <div><strong>{trigger.type?.name || trigger.type?.key || trigger.type}</strong></div>
                            <div className='text-muted'>{trigger.id ? `Trigger ID: ${trigger.id}` : ''}</div>
                        </div>
                        <div className='small text-break mb-2'>
                            {trigger.type?.description || trigger.type?.key || JSON.stringify(trigger.type)}
                        </div>
                        {formatTriggerProperties(trigger.properties || trigger.properties || [])}
                    </div>
                ))}
            </div>

            <div className='mb-3'>
                <h5>Actions</h5>
                {tasks.length === 0 && <div className='text-muted'>No actions configured.</div>}
                {tasks.map((task, index) => (
                    <div key={task.id || index} className='mb-3 p-2 rounded border'>
                        <div className='d-flex justify-content-between'>
                            <div><strong>{task.taskName || task.taskType?.type || task.taskType?.name || `Task ${index + 1}`}</strong></div>
                            <div className='text-muted'>{task.id ? `Task ID: ${task.id}` : ''}</div>
                        </div>
                        <div className='small text-break mb-2'>
                            {_event?.name && `Workflow action for ${_event.name}`}
                        </div>
                        {formatTaskProperties(task.properties || task.properties || [])}
                    </div>
                ))}
            </div>
        </div>
    )
}

const LOG_SORT_OPTIONS = [
    { name: 'triggeredDate', label: 'Triggered Date' },
    { name: 'completedDate', label: 'Completed Date' },
    { name: 'workflowName', label: 'Workflow Name' },
    { name: 'workflowCategory', label: 'Category' },
    { name: 'status', label: 'Status' },
]

const parseDateValue = (value) => {
    if (!value) return null
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.getTime()
}

const getSortableValue = (row, key) => {
    const value = row?.[key]
    if (value === undefined || value === null) return ''
    if (key.toLowerCase().includes('date')) return parseDateValue(value) ?? ''
    if (typeof value === 'string') return value.toLowerCase()
    return value
}

export default function WorkflowBreakdown() {
    const [workflows, setWorkflows] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [activeTab, setActiveTab] = useState('breakdown')
    const [logs, setLogs] = useState(null)
    const [logLoading, setLogLoading] = useState(false)
    const [logError, setLogError] = useState(null)
    const [logSortBy, setLogSortBy] = useState('triggeredDate')
    const [logSortDirection, setLogSortDirection] = useState('desc')

    useEffect(() => {
        const loadWorkflows = async () => {
            setLoading(true)
            setError(null)
            try {
                const facilityId = Authentication.getFacility()
                const groups = await WorkFlow.ListGroups(facilityId)
                const groupEvents = await Promise.all(groups.map(async (group) => {
                    const events = await WorkFlow.ListCategoryEvents(group.id)
                    return events.map((event) => ({ category: group, event }))
                }))
                setWorkflows(groupEvents.flat())
            } catch (e) {
                console.error('WorkflowBreakdown load error', e)
                setError(e?.message || 'Unable to load workflow breakdown.')
            } finally {
                setLoading(false)
            }
        }
        loadWorkflows()
    }, [])

    useEffect(() => {
        if (activeTab !== 'logs' || logs || logLoading) return
        const loadLogs = async () => {
            try {
                setLogLoading(true)
                setLogError(null)
                const entries = await Promise.all(workflows.map(async ({ category, event }) => {
                    try {
                        const eventLogs = await WorkFlow.GetWorkFlowLogs(event.id)
                        return (Array.isArray(eventLogs) ? eventLogs : []).map((log) => ({
                            ...log,
                            workflowName: event.name,
                            workflowCategory: category?.name || 'Unknown',
                        }))
                    } catch (e) {
                        return []
                    }
                }))
                setLogs(entries.flat())
            } catch (e) {
                console.error('WorkflowBreakdown log load error', e)
                setLogError(e?.message || 'Unable to load workflow logs.')
            } finally {
                setLogLoading(false)
            }
        }
        if (workflows.length > 0) loadLogs()
    }, [activeTab, logs, logLoading, workflows])

    const sortedLogs = useMemo(() => {
        if (!logs) return null
        const sorted = [...logs].sort((a, b) => {
            const aValue = getSortableValue(a, logSortBy)
            const bValue = getSortableValue(b, logSortBy)
            if (aValue === bValue) return 0
            if (aValue === '' || aValue === null) return 1
            if (bValue === '' || bValue === null) return -1
            if (typeof aValue === 'number' && typeof bValue === 'number')
                return aValue - bValue
            return aValue > bValue ? 1 : -1
        })
        if (logSortDirection === 'desc') sorted.reverse()
        return sorted
    }, [logs, logSortBy, logSortDirection])

    const renderLogsTab = () => {
        if (logLoading) return <LoaderComponent />
        if (logError) return <div className='alert alert-danger'>{logError}</div>
        if (!logs) return <div className='text-muted'>Select the Logs tab to load workflow logs.</div>
        if (logs.length === 0) return <div className='alert alert-secondary'>No workflow logs found.</div>

        return (
            <div>
                <div className='d-flex flex-wrap align-items-center gap-3 mb-3'>
                    <div><strong>{sortedLogs.length}</strong> log entries collected from {workflows.length} workflows.</div>
                    <div className='d-flex align-items-center gap-2'>
                        <label className='mb-0'>Order by</label>
                        <select className='form-select form-select-sm' value={logSortBy} onChange={(e) => setLogSortBy(e.target.value)}>
                            {LOG_SORT_OPTIONS.map((option) => (
                                <option key={option.name} value={option.name}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    <button type='button' className='btn btn-outline-secondary btn-sm' onClick={() => setLogSortDirection((dir) => dir === 'desc' ? 'asc' : 'desc')}>
                        {logSortDirection === 'desc' ? 'Newest first' : 'Oldest first'}
                    </button>
                </div>
                <LogsTableComponent
                    logs={sortedLogs}
                    options={{
                        columns: [
                            { name: 'workflowCategory', type: 'STRING', human: 'Category' },
                            { name: 'workflowName', type: 'STRING', human: 'Workflow' },
                            { name: 'triggeredDate', type: 'DATETIME', human: 'Triggered Date' },
                            { name: 'completedDate', type: 'DATETIME', human: 'Completed Date' },
                            { name: 'eventName', type: 'STRING', human: 'Event Name' },
                            { name: 'eventTask', type: 'STRING', human: 'Task' },
                            { name: 'triggeredFor', type: 'STRING', human: 'For' },
                            { name: 'triggeredBy', type: 'STRING', human: 'By' },
                            { name: 'description', type: 'STRING', human: 'Description' },
                            { name: 'executedDateTime', type: 'DATETIME', human: 'Executed At' },
                            { name: 'status', type: 'STATUS', human: 'Status' },
                        ]
                    }}
                />
            </div>
        )
    }

    return (
        <div className='p-4' style={{ overflow: 'auto' }}>
            <div className='mb-4'>
                <h1>Workflow Breakdown</h1>
                <p className='text-muted'>Expanded view of all workflows, triggers, actions, and configured properties for {Authentication.getFacilityName() || 'this facility'}.</p>
            </div>

            <div className='mb-4'>
                <div className='btn-group' role='group'>
                    <button type='button' className={`btn ${activeTab === 'breakdown' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setActiveTab('breakdown')}>Breakdown</button>
                    <button type='button' className={`btn ${activeTab === 'logs' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setActiveTab('logs')}>Workflow Logs</button>
                </div>
            </div>

            {activeTab === 'breakdown' && (
                <>
                    {loading && <LoaderComponent />}
                    {error && <div className='alert alert-danger'>{error}</div>}
                    {!loading && !error && workflows.length === 0 && <div className='alert alert-secondary'>No workflows found.</div>}
                    {!loading && !error && workflows.map((workflow, index) => (
                        <WorkflowItem key={`${workflow.event?.id || index}-${index}`} category={workflow.category} event={workflow.event} />
                    ))}
                </>
            )}

            {activeTab === 'logs' && renderLogsTab()}
        </div>
    )
}
